/**
 * Raptor Predator Frontend - Application Entry Point
 * 
 * Initializes the React application with all providers:
 * - TanStack Query for data fetching
 * - Socket.io for real-time updates
 * - Error boundaries for graceful error handling
 */

// ============================================================================
// Polyfill: crypto.randomUUID for non-secure contexts (HTTP)
// Required for development/testing over HTTP without HTTPS
// ============================================================================
if (typeof crypto !== 'undefined' && typeof crypto.randomUUID !== 'function') {
  crypto.randomUUID = function(): `${string}-${string}-${string}-${string}-${string}` {
    const hex = '0123456789abcdef';
    let uuid = '';
    for (let i = 0; i < 36; i++) {
      if (i === 8 || i === 13 || i === 18 || i === 23) {
        uuid += '-';
      } else if (i === 14) {
        uuid += '4'; // Version 4
      } else if (i === 19) {
        uuid += hex[(Math.random() * 4) | 8]; // Variant
      } else {
        uuid += hex[(Math.random() * 16) | 0];
      }
    }
    return uuid as `${string}-${string}-${string}-${string}-${string}`;
  };
}

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { ErrorBoundary } from 'react-error-boundary';

import { AppRouter } from './router';
import { queryClient } from './lib/queryClient';
import { ErrorFallback } from './components/ui/ErrorFallback';
import { DemoModeProvider } from './contexts/DemoModeContext';
import { shouldConnectToApi, isDemoModeEnabled } from './utils/environment';

import './index.css';

// ============================================================================
// Application Initialization
// ============================================================================

// CRITICAL FIX: Socket initialization is now DEFERRED and CONDITIONAL
// - In demo mode: NO socket connection (no API calls)
// - In production with API: Socket connects after auth
// 
// Socket is initialized lazily via useEffect in components that need it,
// NOT at module load time. This prevents unwanted API calls in demo mode.

// Log environment status for debugging
console.log('[Main] Environment check:', {
  demoModeEnabled: isDemoModeEnabled(),
  shouldConnectToApi: shouldConnectToApi(),
  VITE_DEMO_MODE: import.meta.env.VITE_DEMO_MODE,
  VITE_MDC_API_URL: import.meta.env.VITE_MDC_API_URL,
});

// Register service worker for PWA
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch((error: unknown) => {
      console.error('Service worker registration failed:', error);
    });
  });
}

// ============================================================================
// Root Component
// ============================================================================

function App(): JSX.Element {
  return (
    <React.StrictMode>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <DemoModeProvider defaultMode={true} persistPreference={true}>
          <QueryClientProvider client={queryClient}>
            <AppRouter />
            <Toaster
              position="bottom-right"
              toastOptions={{
                className: 'bg-slate-800 text-white border border-slate-700',
                duration: 4000,
                style: {
                  background: 'rgb(30, 41, 59)',
                  color: 'white',
                  border: '1px solid rgb(51, 65, 85)',
                },
              }}
            />
            {import.meta.env.DEV && (
              <ReactQueryDevtools initialIsOpen={false} position="bottom" />
            )}
          </QueryClientProvider>
        </DemoModeProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
}

// ============================================================================
// Render Application
// ============================================================================

const rootElement = document.getElementById('root');
if (rootElement === null) {
  throw new Error('Root element not found');
}

ReactDOM.createRoot(rootElement).render(<App />);
